import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AssetDto, Section } from '@nusign/picflow-backend-types';
import { combineLatest, distinctUntilChanged, distinctUntilKeyChanged, filter, map, switchMap } from 'rxjs';
import { AdvancedModeComponent } from '../../../gallery-shared/components/advanced-mode/advanced-mode.component';
import { ApprovalLimitInfoComponent } from '../../../gallery/components/approval-limit-info/approval-limit-info.component';
import { calcLegacyTheme } from '../../../gallery/lib/calc-legacy-theme';
import { getFullPath } from '../../../lib/get-full-path';
import { includeIsLiked } from '../../../lib/include-is-liked';
import { objectsMatch } from '../../../lib/objects-match';
import { AssetService } from '../../../services/asset.service';
import { GalleryService } from '../../../services/gallery.service';
import { SectionService } from '../../../services/section.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { ViewGalleryHeaderComponent } from '../../components/view-gallery-header/view-gallery-header.component';

@Component({
  selector: 'app-view-gallery-advanced-mode-page',
  templateUrl: './view-gallery-advanced-mode-page.component.html',
  styleUrls: ['./view-gallery-advanced-mode-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AdvancedModeComponent,
    ViewGalleryHeaderComponent,
    ApprovalLimitInfoComponent,
    LoadingIndicatorComponent,
  ],
})
export class ViewGalleryAdvancedModePageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sectionService = inject(SectionService);
  private galleryService = inject(GalleryService);
  private assetService = inject(AssetService);
  private platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  private galleryId$ = this.route.parent.data.pipe(
    map(data => data.gallery.id),
    distinctUntilChanged()
  );
  private galleryId = toSignal(this.galleryId$);

  gallery$ = this.galleryId$.pipe(switchMap(id => this.galleryService.getGallery(id)));
  gallery = toSignal(this.gallery$);
  public galleryPermissions$ = toObservable(this.gallery).pipe(
    switchMap(gallery => this.galleryService.getGalleryPermissions(gallery))
  );
  public galleryPermissions = toSignal(this.galleryPermissions$);
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return gallery.theme || calcLegacyTheme(gallery);
  });
  sections = this.isBrowser
    ? toSignal(this.galleryId$.pipe(switchMap(galleryId => this.sectionService.getSections(galleryId))))
    : signal<Section[]>([]);
  assets = this.isBrowser
    ? toSignal(
        combineLatest([
          this.gallery$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
          this.galleryPermissions$.pipe(filter(Boolean), distinctUntilChanged(objectsMatch)),
        ]).pipe(
          switchMap(([gallery, permissions]) =>
            this.assetService.getAssetsForGallery(gallery.id, {
              sorted: true,
              includeTags: true,
              includeIsLiked: includeIsLiked(permissions, gallery),
            })
          )
        )
      )
    : signal<AssetDto[]>([]);
  onAssetPage = toSignal(this.router.events.pipe(map(() => getFullPath(this.route.snapshot).pop() !== 'advanced')));
  private loadingGallery = linkedSignal(() => !!this.galleryId());
  private loadingSections = linkedSignal(() => !!this.galleryId());
  private loadingAssets = linkedSignal(() => !!this.galleryId());

  loading = computed(() => {
    const loadingGallery = this.loadingGallery();
    const loadingSections = this.loadingSections();
    const loadingAssets = this.loadingAssets();
    return loadingGallery || loadingSections || loadingAssets;
  });

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    effect(() => this.loadingGallery.set(!this.gallery()));
    effect(() => this.loadingSections.set(!this.sections()));
    effect(() => this.loadingAssets.set(!this.assets()));
  }
}
